import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { InvoiceTodoSelectionFormContainerProps, InvoiceTodoView } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import InvoiceTodoSelectionForm from "./todoSelectionForm";
import useInvoiceTodoFormHelpers from "./useTodoFormHelpers";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { InvoiceHelper } from "utils/helpers";

const InvoiceTodoSelectionFormContainer: ForwardRefRenderFunction<
  FormHandles,
  InvoiceTodoSelectionFormContainerProps
> = (props, ref) => {
  const invoiceService = useInvoiceService();

  const { invoice, invoiceDateChangeHandler, onInvoiceSendMethodSelected, view } = props;
  const { getValidationSchema, initialValues } = useInvoiceTodoFormHelpers(invoice);
  const [nextInvoiceSerialNumber, setNextInvoiceSerialNumber] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (invoice?.serialNumber) {
      setNextInvoiceSerialNumber(InvoiceHelper.formatInvoiceNumber(invoice.serialNumber));
      return;
    }

    invoiceService.getInvioceNextSerialNumber().then((nextSerialNumber) => {
      setNextInvoiceSerialNumber(InvoiceHelper.formatInvoiceNumber(nextSerialNumber));
    });
  }, []);

  const handleSubmit = async (values: InvoiceFormTypes.Todo) => {
    const id = await invoiceService.createOrUpdateInvoice(
      invoice?.id,
      invoiceService.convertTodoFormToAPIRequest(values, invoice!)
    );
    if (id)
      switch (values.sendType) {
        case WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent:
          await invoiceService.markAsSent(id);
          await invoiceService.downloadPdf(id);
          break;
        case WintSuperkollAPIModel.InvoiceSendMethod.Email:
          const options: WintSuperkollAPIModel.InvoiceMailOptions = {
            from: {
              type: values.sendMail?.emailFromType ?? WintSuperkollAPIModel.SendInvoiceMailsFromEnum.PersonMail,
              address: values.sendMail?.emailFrom,
            },
            to: {
              type: InvoiceHelper.getEmailToType(values.mailOptions?.defaultEmailTo, values.sendMail?.emailTo),
              address: values.sendMail?.emailTo
                ?.map((to) => to?.value)
                .filter((email) => email)
                .join(","),
            },
            subject: InvoiceHelper.revertSerialNumberToTemplate(values.sendMail?.emailSubject, nextInvoiceSerialNumber),
            message: values.sendMail?.emailMessage,
          };
          await invoiceService.sendAsEmail(id, options);
          break;
        case WintSuperkollAPIModel.InvoiceSendMethod.Print:
          await invoiceService.sendAsPrint(id);
          break;
      }
  };

  return (
    <InvoiceTodoSelectionForm
      ref={ref}
      {...props}
      validationSchema={getValidationSchema(view === InvoiceTodoView.PreviewForm)}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      invoiceDateChangeHandler={invoiceDateChangeHandler}
      onInvoiceSendMethodSelected={onInvoiceSendMethodSelected}
      nextInvoiceSerialNumber={nextInvoiceSerialNumber}
    />
  );
};

export default forwardRef(InvoiceTodoSelectionFormContainer);
